import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { Link, useNavigate } from 'react-router'

//evertel
import { useService } from '@evertel/di'
import { Links } from '@evertel/constants'
import { Analytics } from '@evertel/analytics'
import { LoginService } from '@evertel/session/LoginService'
import { SessionService } from '@evertel/session/SessionService'
import { AppLoader, Card, CardBody, Col, InfoBox, Row, useUI } from '@evertel/web/ui'

const getAccessTokenFromURL = () => {
    const searchParams = new URLSearchParams(window.location.search)
    const queryToken = searchParams.get('access_token')
    if (queryToken) return queryToken

    return window.location.hash.split('&')[0]?.replace('#access_token=', '') || null
}

const LoginSSO: React.FC = observer(() => {

    const analytics = useService(Analytics)
    const loginSevice = useService(LoginService)
    const sessionService = useService(SessionService)

    const navigate = useNavigate()

    const { addToast } = useUI()

    const [error, setError] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    const accessTokenSSOParam = getAccessTokenFromURL()

    useEffect(() => {
        analytics.logView('login-sso')
    }, [])

    useEffect(() => {
        if (sessionService.currentUserId) {
            //user is already logged in?  go to landing?
            navigate('/landing', { replace: true })
        }
    }, [sessionService.currentUserId])

    useEffect(() => {
        if (accessTokenSSOParam) {
            doLogin()
        } else {
            navigate('/login')
        }
    }, [accessTokenSSOParam])

    const handleAutoJoinFailed = () => {
        setTimeout(() => {
            navigate('/account/departments/join')
        }, 1500)
    }

    const doLogin = async () => {
        if (!accessTokenSSOParam) {
            return
        }

        setIsLoading(true)

        try {
            const result = await loginSevice.authenticateUser(accessTokenSSOParam)

            //Login success

            // GCWSCSR-915: agency full — user is logged in but was NOT added
            // to their agency. Show a persistent notice on the way in.
            if (result?.enterpriseJoinNotice) {
                const { departmentName } = result.enterpriseJoinNotice
                addToast({
                    color: 'warning',
                    autoClose: false,
                    message: `You're logged in, but ${departmentName || 'your agency'} has no available member seats, so you have not been added to the agency. Contact your agency administrator about adding seats.`
                })
            }
        } catch (loginError:any) {
            if (loginError.code === 'AUTO_JOIN_FAILED') {
                handleAutoJoinFailed()
                return
            }

            // Generic error
            setError(loginError.message)
        } finally {
            setTimeout(() => {
                setIsLoading(false)
            }, 600)
        }
    }

    return (
        <>
            {isLoading && <AppLoader isOpen={isLoading} type="full-overlay"/>}
            <Row className="login auth-layout animated">
                <Col
                    valign="center"
                    align="center"
                    className="p-md-5 p-3"
                    style={{ minHeight: '100vh', margin: 'auto', overflow: 'auto', maxWidth: 600 }}
                >
                    <Card className="p-4 fadeIn animated login-card" style={{ width: '100%' }}>
                        <CardBody className="text-center">
                            <Col align="center">
                                <img
                                    src={'/assets/img/connect-logo-110x110.png'}
                                    width="110"
                                    alt="Evertel"
                                    className="mb-4"
                                />
                                {error && (
                                    <InfoBox color="danger" className="mb-3" style={{ width: '100%', maxWidth: '100%' }}>
                                        {error}
                                    </InfoBox>
                                )}
                                {isLoading ?
                                    <h2>Logging you in...</h2>
                                    :
                                    <Link
                                        to={'/login'}
                                        className="btn btn-link btn-lg">
                                        Back to Login
                                    </Link>
                                }
                                <div className="mt-4">
                                    <small className="fs-6">
                                        You are accessing a restricted information system. Do not login to Evertel on public computers or on unsecured networks.
                                        System usage may be monitored, recorded, and subject to audit. Unauthorized use of the system is prohibited and may be subject to criminal and/or civil
                                        penalties. Use of the system indicates consent to monitoring and recording.
                                        |
                                    </small>
                                    <a href={Links.helpCenter} className="fs-5" target="_blank" rel="noopener noreferrer">
                                        <small> Help Center </small>
                                    </a>
                                </div>
                            </Col>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </>
    )
})

export { LoginSSO }
